<script>
import BaseChip from '@/components/_base-chip.vue'
import UserAvatar from '@/components/user/user-avatar.vue'

export default {
  name: 'BookingDetailsTab',
  components: {
    UserAvatar,
    BaseChip,
  },
  props: {
    booking: { type: Object, default: null },
    display: { type: Function, required: true },
  },
  computed: {
    replacingPerson() {
      const replacing = this.booking?.replacing
      if (!replacing) return null
      // Validate that we have at least one name field
      if (!replacing.firstName && !replacing.lastName) return null
      return replacing
    },
    replacingName() {
      if (!this.replacingPerson) return null
      return this.compileReplacingName(this.replacingPerson)
    },
    bookingDetails() {
      const b = this.booking || {}
      const grades = Array.isArray(b.grades)
        ? b.grades.map((g) => g?.title).filter(Boolean)
        : []
      return {
        grade: this.display(grades.join(', ')),
        room: this.display(b.roomNumber),
        reason: this.display(b.reason),
        bookingId: this.display(b.id != null ? `#${b.id}` : null),
        certifications:
          Array.isArray(b.certificationTypes) && b.certificationTypes.length
            ? b.certificationTypes
            : ['-'],
        internalNotes: this.display(b.clientNotes),
        educatorNotes: this.display(b.notes),
        purchaseOrderNumber: this.display(b.purchaseOrderNumber),
      }
    },
  },
  methods: {
    /**
     * Compiles a full name from a person object
     * @param {{id: number, firstName?: string, lastName?: string}} person
     * @returns {string|null}
     */
    compileReplacingName(person) {
      if (!person) return null
      const firstName = person.firstName?.trim() || ''
      const lastName = person.lastName?.trim() || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || null
    },
  },
}
</script>

<template>
  <!-- DETAILS TAB -->
  <v-tab-item>
    <div class="details-grid px-3">
      <div class="row">
        <div class="label">{{ $t('sidePanel.grade') }}</div>
        <div class="value">{{ bookingDetails.grade }}</div>
      </div>
      <div class="row">
        <div class="label">{{ $t('sidePanel.room') }}</div>
        <div class="value">{{ bookingDetails.room }}</div>
      </div>
      <div class="row">
        <div class="label">{{ $t('sidePanel.replacing') }}</div>
        <div v-if="replacingPerson" class="value">
          <UserAvatar size="sm" :name="replacingName" />
          <div class="ml-2">
            <p class="mb-0 font-weight-medium">{{ replacingName }}</p>
          </div>
        </div>
        <div v-else class="value">
          -
        </div>
      </div>
      <div class="row">
        <div class="label">{{ $t('sidePanel.reason') }}</div>
        <div class="value">{{ bookingDetails.reason }}</div>
      </div>
      <div class="row">
        <div class="label">{{ $t('sidePanel.bookingId') }}</div>
        <div class="value">{{ bookingDetails.bookingId }}</div>
      </div>
      <div class="row">
        <div class="label">{{ $t('sidePanel.certification') }}</div>
        <v-chip-group column class="value certification-chips">
          <BaseChip
            v-for="(certification, index) in bookingDetails.certifications"
            :key="`booking-certificate-${index}`"
            :variant="index % 2 === 0 ? 'blue' : 'purple'"
          >
            {{ certification }}
          </BaseChip>
        </v-chip-group>
      </div>

      <div class="row">
        <div class="label align-start">
          {{ $t('sidePanel.internalNotes') }}
        </div>
        <div class="value ws-pre-wrap">{{ bookingDetails.internalNotes }}</div>
      </div>
      <div class="row">
        <div class="label align-start">
          {{ $t('sidePanel.purchaseOrderNumber') }}
        </div>
        <div class="value ws-pre-wrap">{{
          bookingDetails.purchaseOrderNumber
        }}</div>
      </div>
    </div>
  </v-tab-item>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/side-panel.scss';

.certification-chips {
  padding: 0;
}
</style>
